import { breakPolicyRuleResponseSchema } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { pendingSettingsSchema } from "@src/appV2/FacilityDrawer/BillingAlerts/types";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type QueryClient } from "@tanstack/react-query";
import { z } from "zod";

export type BreakPolicyRule = z.infer<typeof breakPolicyRuleResponseSchema>;

export const useGetFacilityProfileResponseSchema = z.object({
  name: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  userId: z.string(),
  quizWorkersOnRules: z
    .custom<boolean | undefined>((value) => {
      if (value === null || value === undefined) {
        return true;
      }

      return typeof value === "boolean";
    })
    .transform((value) => (value === null ? undefined : value)),
  maxAllowedWorkHoursPerWeek: z.number().nullish(),
  maxAllowedWorkConsecutiveHours: z.number().nullish(),
  pendingSettings: pendingSettingsSchema.nullish(),
  tmz: z.string().nullish(),
  status: z.string().nullish(),
  variableCharge: z
    .object({
      enabled: z.boolean().optional(),
    })
    .nullish(),
  placementSettings: z
    .object({
      managedByParentWorkplace: z.boolean().optional(),
      isProspect: z.boolean().optional(),
    })
    .optional(),
  isHiringOnly: z.boolean().optional(),
  parentFacilityId: z.string().nullish(),
  isEarlyClockInEnabled: z.boolean().optional(),
  mapleIntegrationEnabled: z.boolean().optional(),
  rateNegotiation: z.string().optional(),
  requiresLunchBreak: z.boolean().optional(),
  requiresLunchBreakSince: z.string().nullish(),
  restrictOverworkShiftInvites: z.boolean().optional(),
  providesRestBreaks: z.boolean().optional(),
  providesRestBreaksSince: z.string().nullish(),
  extraWorkedTimeConfig: z
    .object({
      BREAK: z
        .object({
          isAutoDeclinationEnabled: z.boolean().optional(),
          autoDeclinationReason: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  verificationPreference: z
    .object({
      preferenceType: z.string().optional(),
    })
    .optional(),
  breakPolicies: z.array(breakPolicyRuleResponseSchema).optional(),
  fullAddress: z
    .object({
      stateCode: z.string().optional(),
      state: z.string().optional(),
      city: z.string().optional(),
      postalCode: z.string().optional(),
      formatted: z.string().optional(),
      streetNumber: z.string().optional(),
      streetName: z.string().optional(),
      region: z.string().optional(),
      country: z.string().optional(),
      countryCode: z.string().optional(),
    })
    .optional(),
  isRequestToBookPerShiftEnabled: z.boolean().optional(),
  firstShiftCreatedAt: z.string().datetime().nullish(),
  shiftFeedbackQrCodeConfiguration: z
    .object({
      pinValidationEnabled: z.boolean().optional(),
      additionalQuestions: z
        .array(
          z.object({
            key: z.string(),
            description: z.string(),
            subText: z.string().optional(),
            options: z.array(
              z.object({
                value: z.string(),
                blocksFormSubmissionWithErrorMessage: z.string().optional(),
              })
            ),
          })
        )
        .optional(),
    })
    .optional(),
  rates: z.record(z.string(), z.number()).optional(),
  autoVerifyThresholdInMinutes: z.number().optional(),
});

export type UseGetFacilityProfileResponse = z.infer<typeof useGetFacilityProfileResponseSchema>;

export interface UseGetFacilityProfileOptions extends UseGetQueryOptions<UseGetFacilityProfileResponse> {
  customHeaders?: Record<string, string>;
}

// Predicate-match every `projection` variant (projection is part of the URL).
// Fire-and-forget so mutation `mutateAsync` stays in sync with the HTTP
// response; callers rely on that for optimistic-update reconciliation.
export function invalidateFacilityProfileQueries(
  queryClient: QueryClient,
  workplaceId: string
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];
      return typeof key === "string" && key.includes(`/facilityprofile/${workplaceId}`);
    },
  });
}

/**
 * Canonical URL builder for the facility-profile endpoint. Exported so other callers
 * (e.g. batched lookups via `useQueries`) can produce an identical cache key without
 * duplicating the URL pattern — keeping cache dedupe structural rather than comment-based.
 */
export function getFacilityProfileUrl(facilityId: string, projection?: string): string {
  const searchParams = new URLSearchParams();
  if (isDefined(projection)) {
    searchParams.set("projection", projection);
  }
  return `${environmentConfig.REACT_APP_BASE_API_URL}/facilityprofile/${facilityId}?${searchParams}`;
}

export function useGetFacilityProfile(
  facilityId: string,
  projection?: string,
  options: UseGetFacilityProfileOptions = {}
) {
  return useGetQuery({
    url: getFacilityProfileUrl(facilityId, projection),
    responseSchema: useGetFacilityProfileResponseSchema,
    meta: {
      logErrorMessage: `${APP_V2_APP_EVENTS.GET_FACILITY_PROFILE_FAILED}`,
    },
    ...options,
  });
}
